import type { VisualPayload } from "../../types";
import CardShell from "./_CardShell";

interface Props { payload: VisualPayload }

interface EvoEntry {
  to_name: string;
  candy_cost?: number;
  requires_item?: string;
  requires_buddy_km?: number;
  requires_trade?: boolean;
}

interface Milestone {
  to_level: number;
  stardust: number;
  candy: number;
  xl_candy: number;
}

function fmt(n: number): string {
  return n.toLocaleString();
}

export default function CandyCostCard({ payload }: Props) {
  const evoCostsSec = payload.sections.find((s) => s.section === "evolution_costs");
  const noEvoSec    = payload.sections.find((s) => s.section === "no_evolution");
  const milestones  = payload.sections.find((s) => s.section === "powerup_milestones");
  const notFound    = payload.sections.find((s) => s.section === "not_found");

  return (
    <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined}>
      {notFound ? (
        <p className="text-sm text-white/40">{(notFound as { reason?: string }).reason ?? "No data found."}</p>
      ) : (
        <div className="space-y-5">

          {/* Evolution candy costs */}
          {evoCostsSec && (
            <div>
              <p className="text-[11px] text-white/30 uppercase tracking-wide mb-2">Evolution Costs</p>
              <ul className="space-y-1.5">
                {(evoCostsSec.evolutions as EvoEntry[]).map((e) => (
                  <li key={e.to_name} className="flex items-center gap-2 py-1 px-2 rounded-lg hover:bg-white/[0.03]">
                    <span className="flex-1 text-sm text-white/80">→ {e.to_name}</span>
                    {e.candy_cost != null && (
                      <span className="text-sm font-semibold text-amber-400">{e.candy_cost} candy</span>
                    )}
                    {e.requires_item && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-300">
                        {e.requires_item}
                      </span>
                    )}
                    {e.requires_buddy_km && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-300">
                        {e.requires_buddy_km} km buddy
                      </span>
                    )}
                    {e.requires_trade && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-500/15 text-green-300">
                        trade
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {noEvoSec && (
            <p className="text-sm text-white/40">{(noEvoSec as { note?: string }).note}</p>
          )}

          {/* Power-up milestones */}
          {milestones && (milestones.milestones as Milestone[]).length > 0 && (
            <div>
              <p className="text-[11px] text-white/30 uppercase tracking-wide mb-2">
                {(milestones as { note?: string }).note ?? "Power-up Milestones"}
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-white/30">
                      <th className="text-left py-1 pr-3 font-medium">Level</th>
                      <th className="text-right py-1 pr-3 font-medium">Stardust</th>
                      <th className="text-right py-1 pr-3 font-medium">Candy</th>
                      <th className="text-right py-1 font-medium">XL Candy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(milestones.milestones as Milestone[]).map((m) => (
                      <tr key={m.to_level} className="border-t border-white/[0.05] hover:bg-white/[0.02]">
                        <td className="py-1.5 pr-3 text-white/70">Lv {m.to_level}</td>
                        <td className="py-1.5 pr-3 text-right text-amber-400/80">{fmt(m.stardust)}</td>
                        <td className="py-1.5 pr-3 text-right text-white/70">{fmt(m.candy)}</td>
                        <td className="py-1.5 text-right text-white/50">{m.xl_candy > 0 ? fmt(m.xl_candy) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      )}
    </CardShell>
  );
}
